// src/pages/PostPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Grid,
  Stack,
  Text,
  Button,
  Group,
  Badge,
  Card,
  Skeleton,
  Modal,
  ScrollArea,
  ActionIcon,
} from '@mantine/core';
import { Carousel } from '@mantine/carousel';
import type { EmblaCarouselType } from 'embla-carousel';
import { IconMail, IconPhone, IconX } from '@tabler/icons-react';
import '@mantine/carousel/styles.css';
import { useParams } from 'react-router';
import type PostInterface from '../../interfaces/PostInterface';
import type PostImageInterface from '../../interfaces/PostImageInterface';
import { serverAccessor } from '../../accessors/serverAccessor';
import { trackEvent } from '../../utils/analytics.ts';
import { useTranslation } from 'react-i18next';
import Seo from '../../components/Seo.tsx';

const BASE_URL = 'https://promonsa.net';

function usePostById(postId?: string) {
  const [post, setPost] = useState<PostInterface | null>(null);
  const [loading, setLoading] = useState<boolean>(!!postId);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!postId) {
      setLoading(false);
      setError('Missing post id');
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const data = await serverAccessor.getPost(postId);
        if (!cancelled) setPost(data);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load post');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [postId]);

  return { post, loading, error };
}

// ---- Image Gallery with carousel, thumbnails, and full-screen viewer ----
function ImageGallery({
  images,
  initialImageId,
  height = 520,
  title,
}: {
  images: PostImageInterface[];
  initialImageId?: string;
  height?: number;
  title?: string;
}) {
  // order ascending; undefined orders go last but stable
  const ordered = useMemo(() => {
    if (!images?.length) return [];
    return [...images].sort((a, b) => {
      const ao = Number.isFinite(a.order) ? a.order : Number.POSITIVE_INFINITY;
      const bo = Number.isFinite(b.order) ? b.order : Number.POSITIVE_INFINITY;
      if (ao !== bo) return ao - bo;
      return String(a.imageId).localeCompare(String(b.imageId));
    });
  }, [images]);

  const initialIndex = useMemo(() => {
    if (!ordered.length || !initialImageId) return 0;
    const idx = ordered.findIndex((i) => i.imageId === initialImageId);
    return idx >= 0 ? idx : 0;
  }, [ordered, initialImageId]);

  const [embla, setEmbla] = useState<EmblaCarouselType | null>(null);
  const [active, setActive] = useState<number>(initialIndex);

  useEffect(() => {
    if (embla) embla.scrollTo(initialIndex);
    setActive(initialIndex);
  }, [embla, initialIndex]);

  // Full-screen viewer
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerEmbla, setViewerEmbla] = useState<EmblaCarouselType | null>(null);

  const getAltText = (index: number) =>
    title ? `${title} photo ${index + 1}` : `Property photo ${index + 1}`;

  const openViewerAt = (index: number) => {
    trackEvent('gallery_viewer_open', {
      category: 'Gallery',
      label: title || 'Property',
      value: index + 1,
    });
    setViewerOpen(true);
    setTimeout(() => viewerEmbla?.scrollTo(index), 0);
  };

  return (
    <Stack gap="sm">
      {/* Main Carousel */}
      <Card radius="md" withBorder padding={0} style={{ overflow: 'hidden' }}>
        <Carousel
          withIndicators
          height={height}
          slideGap="sm"
          emblaOptions={{ loop: true }}
          getEmblaApi={setEmbla}
          onSlideChange={setActive}
          styles={{ indicator: { width: 8, height: 8 } }}
        >
          {ordered.map((img, idx) => {
            const altText = getAltText(idx);
            return (
              <Carousel.Slide key={img.imageId}>
                <img
                  src={img.url}
                  alt={altText}
                  style={{ width: '100%', height, objectFit: 'cover', display: 'block', cursor: 'zoom-in' }}
                  onClick={() => openViewerAt(idx)}
                />
              </Carousel.Slide>
            );
          })}
        </Carousel>
      </Card>

      {/* Thumbnails */}
      <ScrollArea type="auto" scrollbarSize={6} scrollHideDelay={0} styles={{ viewport: { paddingBottom: 4 } }}>
        <Group gap="xs" wrap="nowrap" align="center" px={2}>
          {ordered.map((img, idx) => {
            const altText = getAltText(idx);
            const isActive = idx === active;
            return (
              <Card
                key={`thumb-${img.imageId}`}
                withBorder
                padding={0}
                onClick={() => embla?.scrollTo(idx)}
                style={{
                  width: 96,
                  minWidth: 96,
                  height: 72,
                  borderRadius: 10,
                  overflow: 'hidden',
                  outline: isActive ? '2px solid var(--mantine-color-blue-5)' : 'none',
                  outlineOffset: -2,
                  cursor: 'pointer',
                }}
              >
                <img src={img.url} alt={altText} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </Card>
            );
          })}
        </Group>
      </ScrollArea>

      {/* Full-screen viewer */}
      <Modal
        opened={viewerOpen}
        onClose={() => setViewerOpen(false)}
        fullScreen
        withCloseButton={false}
        radius={0}
        padding={0}
        styles={{ content: { backgroundColor: 'black', padding: 0 }, body: { padding: 0 } }}
      >
        <ActionIcon
          variant="light"
          size="lg"
          onClick={() => setViewerOpen(false)}
          style={{ position: 'fixed', top: 12, right: 12, zIndex: 2, background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(4px)' }}
          aria-label="Close viewer"
        >
          <IconX />
        </ActionIcon>

        <Carousel
          withIndicators
          height="100dvh"
          slideGap="md"
          emblaOptions={{ loop: true }}
          getEmblaApi={setViewerEmbla}
          styles={{ container: { height: '100dvh' }, indicator: { backgroundColor: 'white' } }}
        >
          {ordered.map((img, idx) => {
            const altText = getAltText(idx);
            return (
              <Carousel.Slide key={`viewer-${img.imageId}`}>
                <img
                  src={img.url}
                  alt={altText}
                  style={{ width: '100%', height: '100dvh', objectFit: 'contain', background: 'black', display: 'block' }}
                />
              </Carousel.Slide>
            );
          })}
        </Carousel>
      </Modal>
    </Stack>
  );
}

export default function PostPage() {
  const { postId } = useParams<{ postId: string }>();
  const { post, loading, error } = usePostById(postId);
  const { t } = useTranslation();

  const handleContactClick = (method: 'phone' | 'email', context: string) => {
    const labelParts = [];
    if (post?.title) labelParts.push(post.title);
    if (context) labelParts.push(context);
    const label = labelParts.join(' | ');
    trackEvent('contact_click', {
      category: 'Contact',
      label: `${method.toUpperCase()}${label ? ` | ${label}` : ''}`,
      postId: post?.postId,
    });
  };

  if (loading) {
    return (
      <>
        <Seo
          title={t('seo.propertyLoadingTitle')}
          canonicalPath={`/post/${postId ?? ''}`}
          noindex
        />
        <Stack gap="md" py="lg">
          <Skeleton height={520} radius="md" />
          <Grid gutter="lg">
            <Grid.Col span={{ base: 12, md: 8 }}>
              <Skeleton height={28} />
              <Skeleton height={16} mt="sm" />
              <Skeleton height={16} mt="sm" />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Skeleton height={180} radius="md" />
            </Grid.Col>
          </Grid>
        </Stack>
      </>
    );
  }

  if (error || !post) {
    return (
      <>
        <Seo
          title={t('seo.propertyUnavailableTitle')}
          description={t('seo.propertyUnavailableDescription')}
          canonicalPath={`/post/${postId ?? ''}`}
          noindex
        />
        <Stack gap="sm" py="lg">
          <Text c="red">{error || 'Post not found'}</Text>
        </Stack>
      </>
    );
  }

  const canonicalPath = `/post/${post.postId}`;
  const cleanDescription = post.description?.replace(/\s+/g, ' ').trim() ?? '';
  const summary =
    cleanDescription.length > 160 ? `${cleanDescription.slice(0, 157)}…` : cleanDescription;
  const images = post.images ?? [];
  const coverImage = images.find((img) => img.imageId === post.coverImageId) || images[0];
  const imageUrls = images.map((img) => img.url).filter(Boolean);

  const seoTitle = t('seo.propertyTitle', { title: post.title });
  const fallbackDescription = t('seo.propertyDescriptionFallback');
  const seoDescription = summary || fallbackDescription;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    name: post.title,
    description: cleanDescription || fallbackDescription,
    url: `${BASE_URL}${canonicalPath}`,
    image: imageUrls.slice(0, 6),
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Granada',
      addressRegion: 'Andalusia',
      addressCountry: 'ES',
    },
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
      url: `${BASE_URL}${canonicalPath}`,
    },
    seller: {
      '@type': 'RealEstateAgent',
      name: 'Promociones Montosa',
    },
    areaServed: {
      '@type': 'Place',
      name: 'Granada, Spain',
    },
  };

  return (
    <>
      <Seo
        title={seoTitle}
        description={seoDescription}
        canonicalPath={canonicalPath}
        image={coverImage?.url}
        type="article"
        structuredData={structuredData}
      />
      <Stack gap="lg" py="lg">
        <ImageGallery images={post.images} initialImageId={post.coverImageId} height={520} title={post.title} />

        <Grid gutter="xl" align="start">
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Stack gap="sm">
              <Group gap="sm" align="center">
                <Text size="xl" fw={700}>{post.title}</Text>
                <Badge variant="light" color="blue">Ref: {post.postId}</Badge>
              </Group>
              <Text c="dimmed" size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {post.description}
              </Text>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <Card withBorder radius="md" padding="lg">
              <Stack gap="xs">
                <Text fw={600} size="lg">Interested?</Text>
                <Text size="sm" c="dimmed">Ask about availability, viewings, or more photos.</Text>

                <Group mt="xs" gap="sm" wrap="wrap">
                  <Button
                    fullWidth
                    leftSection={<IconPhone size={16} />}
                    component="a"
                    href="tel:+34653784511"
                    aria-label="Call Promonsa at +34 653 784 511"
                    onClick={() => handleContactClick('phone', 'Post contact card')}
                  >
                    Call +34 653 784 511
                  </Button>
                  <Button
                    fullWidth
                    variant="light"
                    leftSection={<IconMail size={16} />}
                    component="a"
                    href={`mailto:info@promonsa.net?subject=${encodeURIComponent('Property enquiry: ' + post.title)}`}
                    aria-label={`Email Promonsa about ${post.title}`}
                    onClick={() => handleContactClick('email', 'Post contact card')}
                  >
                    Email info@promonsa.net
                  </Button>
                </Group>

                <Text size="xs" c="dimmed" mt="xs">
                  Or email <a href="mailto:info@promonsa.net" aria-label="Email Promonsa" onClick={() => handleContactClick('email', 'Post contact inline link')}>info@promonsa.net</a> anytime.
                </Text>
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>
      </Stack>
    </>
  );
}
